import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { formatTimeStamp } from '../utils/util';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import BoltIcon from '@mui/icons-material/Bolt';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import Button from '@mui/material/Button';
import Notiflix from 'notiflix';
import Tooltip from '@mui/material/Tooltip'

type Anchor = 'top' | 'left' | 'bottom' | 'right';

interface Props {
  anchor: Anchor;
  updateJsonView: any;
}

const LoadData = forwardRef(({ anchor, updateJsonView }: Props, ref: any) => {
  const [state, setState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });
  const [currData, setCurrData] = useState([]);
  const initialRef: any = null;
  const reference = useRef(initialRef);

  useEffect(() => {
    try {
      const storedData = JSON.parse(
        localStorage.getItem('dataskopHistory') as any
      );
      
      if(storedData) {
        storedData.sort((a: any, b: any) => b.timestamp - a.timestamp); // Sort them first in descending order
      }

      setCurrData(storedData);
    } catch (error) {
      Notiflix.Notify.failure(`Error: ${error}`);
    }
  }, []);

  /**
   * Needed in order to expose the method out to the parent component
   * Guide: https://blog.logrocket.com/underrated-react-hooks-youre-missing-out-on/
   */
  useImperativeHandle(ref, () => ({
    oD: () => openDrawer(),
    updateD: () => updateD(),
  }));

  /**
   * This function removes the session that was clicked by the user from the storage.
   * @param timestamp The timestamp of the current session which should be unique
   */
  const removeSession = (timestamp: number) => {
    Notiflix.Confirm.show(
      'Clear Session',
      `Removing session ${formatTimeStamp(timestamp)}. \nShould we proceed?`,
      'Yes',
      'No',
      () => {
        try {
          const storedData = JSON.parse(
            localStorage.getItem('dataskopHistory') as any
          );
          const cleanedData = storedData.filter(
            (e: any) => e.timestamp !== timestamp
          );

          cleanedData.sort((a: any, b: any) => b.timestamp - a.timestamp); // Sort them first in descending order

          // Update the local component
          setCurrData(cleanedData);

          // Also update the local storage not only the component
          localStorage.setItem('dataskopHistory', JSON.stringify(cleanedData));
        } catch (error) {
          Notiflix.Notify.failure(`Error: ${error}`);
        }
      }
    );
  };

  /**
   * Removes the complete history if we want to.
   */
  const clearAll = (): void => {
    Notiflix.Confirm.ask(
      'Delete Session?',
      `You are about to delete all previous session are you sure you want to do this?<br/>
       If yes enter <mark><strong>Absolutely sure!</strong></mark> into the box below and press <strong>"Ok"</strong>.<br/>
       Otherwise <strong>"Cancel"</strong>`,
      'Absolutely sure!',
      'Ok',
      'Cancel',
      () => {
        localStorage.removeItem('dataskopHistory');
        setCurrData([]);
        Notiflix.Notify.success('All previous sessions removed.');
      },
      () => {
        return;
      },
      {
        width: '500px',
        messageMaxLength: 1000,
        plainText: false,
      }
    );
  };

  /**
   * Just needed to trigger the button that is hidden and opens the drawer.
   * Because toggleDrawer was just not callable as it seems.
   */
  const openDrawer = () => {
    reference?.current?.click();
  };

  /**
   * This funciton is used to udpate the data when we add a new one.
   */
  const updateD = () => {
    try {
      const storedData = JSON.parse(
        localStorage.getItem('dataskopHistory') as any
      );
      storedData.sort((a: any, b: any) => b.timestamp - a.timestamp); // Sort them first in descending order

      setCurrData(storedData);
    } catch (error) {
      Notiflix.Notify.failure(`Error: ${error}`);
    }
  };

  const toggleDrawer =
    (anchor: Anchor, open: boolean) =>
    (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }

      setState({ ...state, [anchor]: open });
    };

  const list = (anchor: Anchor) => (
    <Box
      sx={{ width: 350 }}
      role='presentation'
      onClick={toggleDrawer(anchor, false)}
      onKeyDown={toggleDrawer(anchor, false)}
    >
      <List>
        <ListItem key='headline' disablePadding>
          <ListItemText inset>
            <h3>Previous Sessions</h3>
          </ListItemText>
          <Tooltip title="Delete All Sessions" arrow followCursor>
            <ListItemButton onClick={() => clearAll()}>
              <DeleteSweepIcon />
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>
      <Divider />
      <List>
        {currData ? (
          currData.map((elm: any) => (
            <ListItem key={elm.timestamp} disablePadding>
              <Tooltip title="Load this Session" arrow>
                <ListItemButton onClick={() => updateJsonView(elm.data)}>
                  <ListItemIcon>
                    <BoltIcon />
                  </ListItemIcon>
                  <ListItemText primary={formatTimeStamp(elm.timestamp)} />
                </ListItemButton>
              </Tooltip>
              <Tooltip title="Delete Session" arrow followCursor>
                <ListItemButton onClick={() => removeSession(elm.timestamp)}>
                  <DeleteForeverIcon />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))
        ) : (
          <ListItem key='nothing' disablePadding>
            <ListItemButton>
              <ListItemIcon>
                <BoltIcon />
              </ListItemIcon>
              <ListItemText secondary='Nothing saved...' />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <div>
      <React.Fragment key={anchor}>
        <Button
          ref={reference}
          onClick={toggleDrawer(anchor, true)}
          style={{ display: 'none' }}
        >
          Hidden
        </Button>
        <Drawer
          anchor={anchor}
          open={state[anchor]}
          onClose={toggleDrawer(anchor, false)}
        >
          {list(anchor)}
        </Drawer>
      </React.Fragment>
    </div>
  );
});

export default LoadData;
